import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  FINANCES_DEMO_ENABLED,
  FinancesSampleSource,
  noteFinancesSample,
  sampleAccounts,
  sampleAction,
  sampleBillables,
  sampleContacts,
  sampleCorrection,
  sampleCredit,
  sampleDocuments,
  sampleKeep,
  sampleExpenses,
  sampleProjects,
  sampleQuotes,
  sampleRefundOf,
  sampleSettings,
  sampleTax,
  sampleVoid,
} from './finances-demo-backend';
import { SampleRefusal } from './finances-demo-corrections';

/**
 * Stands in for the Finances endpoints `backend/api` does not have yet. Rules and data live in
 * {@link ./finances-demo-backend.ts}; this file is the wiring.
 *
 * Two rules, both self-cancelling:
 * 1. It acts only on the Finances page (`/finances…`); every other page's requests pass through untouched.
 * 2. The real request always goes out; only a 404 ("no such endpoint") is answered from samples. Status 0,
 *    401, 403 and 5xx reach the page as they are. So the page moves to the real backend endpoint by endpoint.
 */

/** `/finances` and its tabs. */
export function onFinancesRoute(routerUrl: string): boolean {
  const path = routerUrl.split(/[?#]/)[0];
  return /^\/finances(\/|$)/.test(path);
}

/** What a request is for this interceptor; `null` = not ours. Exported for the spec. */
export interface FinancesKind {
  source: FinancesSampleSource;
  /** The sample body; a promise when the answer needs a lazily loaded part (the tax rules). */
  answer: () => unknown | Promise<unknown>;
}

const READS: Record<string, [FinancesSampleSource, () => unknown]> = {
  settings: ['settings', sampleSettings],
  accounts: ['accounts', sampleAccounts],
  contacts: ['contacts', sampleContacts],
  projects: ['projects', sampleProjects],
  documents: ['documents', sampleDocuments],
  quotes: ['quotes', sampleQuotes],
  billables: ['billables', sampleBillables],
  expenses: ['expenses', sampleExpenses],
};

export function classify(req: HttpRequest<unknown>): FinancesKind | null {
  let path: string;
  try {
    path = new URL(req.url, 'http://x').pathname;
  } catch {
    path = req.url.split('?')[0];
  }
  const m = path.match(/\/finances\/([^/]+)\/(.+)$/);
  if (!m) return null;
  const rest = m[2];
  const method = req.method;

  if (method === 'GET' && READS[rest]) {
    const [source, answer] = READS[rest];
    return { source, answer };
  }
  // Corrective invoices (README WP9): these change the visit's documents.
  const corr = method === 'POST' ? rest.match(/^documents\/([^/]+)\/(corrections|void|credits|refunds|keep-credit)$/) : null;
  if (corr) {
    const id = decodeURIComponent(corr[1]);
    const answer: Record<string, () => unknown> = {
      corrections: () => sampleCorrection(id, req.body),
      void: () => sampleVoid(id),
      credits: () => sampleCredit(id, req.body),
      refunds: () => sampleRefundOf(id, req.body),
      'keep-credit': () => sampleKeep(id),
    };
    return { source: 'actions', answer: answer[corr[2]] };
  }
  // Actions on documents, quotes, work and expenses; drafting an invoice.
  if (method === 'POST' && /^(documents|quotes|expenses)\/[^/]+\/(reminders|payments|approve|send|renew|withdraw|project|hold)$/.test(rest)) {
    return { source: 'actions', answer: sampleAction };
  }
  if (method === 'POST' && (/^billables\/[^/]+\/accept$/.test(rest) || rest === 'invoices')) return { source: 'actions', answer: sampleAction };
  // The default tax for a draft invoice or quote (read-only: nothing is saved).
  if (method === 'POST' && rest === 'tax/suggest') return { source: 'tax', answer: () => sampleTax(req.body) };
  return null;
}

export const financesDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!FINANCES_DEMO_ENABLED) return next(req);
  const kind = classify(req);
  if (!kind) return next(req);

  // inject() only works here, in the injection context.
  const router = inject(Router);
  const url = router.getCurrentNavigation()?.finalUrl?.toString() ?? router.url;
  if (!onFinancesRoute(url)) return next(req);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // 404 = no such endpoint yet; anything else is a real failure the page must see.
      if (error.status !== 404) return throwError(() => error);
      noteFinancesSample(kind.source);
      // A sample action the server would refuse answers as the server does (409).
      return from(Promise.resolve().then(() => kind.answer())).pipe(
        map(body => new HttpResponse({ status: 200, body, url: req.url })),
        catchError((e: unknown) => throwError(() => (e instanceof SampleRefusal ? new HttpErrorResponse({ status: 409, statusText: e.message, url: req.url }) : e))),
      );
    }),
  );
};
